import { EventWithDetail } from "@garther/interfaces";
import { Badge, Box, Button, Image } from "@garther/ui/components";
import { cn, useI18n } from "@garther/ui/libs";
import { Calendar, MapPin, Users } from "lucide-react";
import { useMemo } from "react";

interface EventCardProps {
  event: EventWithDetail;
  onSelect: (id: string) => void;
  onBook: (id: string) => void;
}

export function EventCard({ event, onSelect, onBook }: EventCardProps) {
  const { formatDate, t, formatCredit } = useI18n();

  // Formatting information
  const { priceDisplay, badgeVariant, formattedTime, formattedDate } = useMemo(() => {
    const price = event.ticketTiers.sort(
      (a, b) => a.price * (a.taken < a.cap ? 1 : 0) - b.price * (b.taken < b.cap ? 1 : 0)
    )[0].price;

    return {
      priceDisplay: price === 0 ? t("event.free") : formatCredit(price),
      badgeVariant: price === 0 ? ("secondary" as const) : ("default" as const),
      formattedTime: formatDate(event.date, { hour: "2-digit", minute: "2-digit" }),
      formattedDate: formatDate(event.date, { weekday: "long", day: "numeric", month: "long" })
    };
  }, [event.date, formatDate]);

  return (
    <Box
      padding="p-0"
      className={cn(
        "flex flex-col overflow-hidden transition-all hover:-translate-y-1 cursor-pointer border-none rounded-none"
      )}
      onClick={() => onSelect(event.id)}
    >
      {/* Image */}
      <div className="relative aspect-[16/9] bg-background-secondary rounded-t-lg overflow-hidden">
        <Image
          src={event.banner || event.featuredImages[0]}
          alt={event.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <Badge
          variant={badgeVariant}
          className={cn(
            "absolute top-4 -right-1 rounded-l-md shadow-sm px-4 py-[4px] text-responsive-sm font-medium drop-shadow-md bg-opacity-90",
            event.booked ? "bg-green-100 text-green-800" : ""
          )}
        >
          {event.booked ? t("event.booked") : priceDisplay}
        </Badge>
      </div>

      {/* Content */}
      <div className="flex flex-col justify-between px-4 pt-3 pb-3 flex-1 space-y-4 border-b border-x border-border rounded-b-lg">
        <div className="space-y-3">
          <div className="space-y-0">
            <h3 className="text-responsive-md font-normal text-text-primary line-clamp-1">{event.title}</h3>
            <p
              className="text-responsive-base text-text-secondary line-clamp-2"
              dangerouslySetInnerHTML={{
                __html: event.description?.replace(/(\r?\n|\r|class="[^"]*"|<br\s*\/?>)+/g, " ")?.trim() || ""
              }}
            />
          </div>

          <div className="space-y-0.5 text-text-secondary">
            <div className="flex items-center gap-2 ">
              <Calendar className="h-4 w-4 shrink-0" />
              <span className="truncate text-responsive-sm font-medium">
                {formattedDate} • {formattedTime}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 shrink-0" />
              <span className="truncate text-responsive-sm font-medium">
                {event.location?.address || "HCM, Vietnam"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 shrink-0" />
              <span className="truncate text-responsive-sm font-medium">
                {t("event.slotsLeft", { count: event.cap - event.taken })}
              </span>
            </div>
          </div>
        </div>
        <Button
          className="w-full"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            onBook(event.id);
          }}
        >
          {t("event.book")}
        </Button>
      </div>
    </Box>
  );
}
